import { PhoneCodes, type PhoneCode } from '@/constants/phone-codes'
import { findCountryByCode } from '../detect/phone-code-auto-detect.helpers'

/**
 * Інтерфейс для розпарсеного значення моделі
 */
export interface ParsedPhoneModel {
  isoCode: string
  phoneCode: string
  number: string
}

/**
 * Composable для роботи з форматуванням телефонних номерів
 * Формат моделі: тільки цифри (380634050363); країну визначаємо по коду
 * Формат відображення: +код номер (наприклад: +380 634050363)
 */
export function usePhoneNumberFormat() {
  /**
   * Парсить значення моделі — тільки цифри (наприклад 380634050363)
   * @param modelValue - Значення моделі (цифри з можливими пробілами/символами — вони відфільтровуються)
   * @returns Розпарсене значення або null якщо недостатньо цифр або країну не визначено
   */
  const parseModelValue = (modelValue: string): ParsedPhoneModel | null => {
    const allDigits = (modelValue ?? '').replace(/\D/g, '')
    if (!allDigits || allDigits.length < 2) {
      return null
    }

    const detected = findCountryByCode(allDigits)
    if (!detected) {
      return null
    }

    const phoneCode = detected.phoneCode.trim()
    const number = allDigits.startsWith(phoneCode)
      ? allDigits.substring(phoneCode.length)
      : allDigits

    return {
      isoCode: detected.isoCode,
      phoneCode,
      number,
    }
  }

  /**
   * Форматує значення в формат моделі — тільки цифри (380634050363)
   */
  const formatToModelValue = (_isoCode: string, phoneCode: string, number: string): string => {
    return (phoneCode + (number ?? '').replace(/\D/g, '')).replace(/\D/g, '')
  }

  /**
   * Перетворює значення моделі в нормалізований номер для відображення (+380 634050363)
   */
  const normalizeToDisplay = (modelValue: string): string => {
    const parsed = parseModelValue(modelValue)
    if (!parsed) return ''
    const cleanNumber = parsed.number.replace(/\D/g, '')
    const full = `+${parsed.phoneCode}${cleanNumber}`
    return parsed.phoneCode && cleanNumber
      ? `+${parsed.phoneCode} ${cleanNumber}`
      : full
  }

  /**
   * Перетворює відображуване значення в формат моделі — тільки цифри (380634050363)
   */
  const formatFromDisplay = (displayValue: string, isoCode: string): string => {
    if (!displayValue?.trim() || displayValue.trim() === '+') return ''
    const country = PhoneCodes.find((c) => c.isoCode === isoCode)
    if (!country) return ''
    const numberWithoutCode = extractNumber(displayValue)
    if (!numberWithoutCode) return ''
    return formatToModelValue(isoCode, country.phoneCode, numberWithoutCode)
  }

  /**
   * Витягує код телефону з відображуваного значення
   * @param displayValue - Відображуване значення (наприклад: "+380634050445")
   * @returns Код телефону або порожній рядок
   */
  const extractPhoneCode = (displayValue: string): string => {
    if (!displayValue?.startsWith('+')) {
      return ''
    }

    const digitsOnly = displayValue.replace(/[^\d]/g, '')

    // Шукаємо код від найдовшого до найкоротшого (4, 3, 2, 1)
    for (let length = 4; length >= 1; length--) {
      const code = digitsOnly.substring(0, length)
      if (PhoneCodes.some((c) => c.phoneCode === code)) {
        return code
      }
    }

    return ''
  }

  /**
   * Витягує номер без коду з відображуваного значення
   * @param displayValue - Відображуване значення (наприклад: "+380634050445")
   * @returns Номер без коду
   */
  const extractNumber = (displayValue: string): string => {
    const phoneCode = extractPhoneCode(displayValue)
    if (!phoneCode) {
      return displayValue.replace(/[^\d]/g, '')
    }

    const digitsOnly = displayValue.replace(/[^\d]/g, '')
    if (digitsOnly.startsWith(phoneCode)) {
      return digitsOnly.substring(phoneCode.length)
    }

    return digitsOnly
  }

  /**
   * Отримує країну по ISO коду
   * @param isoCode - ISO код країни (наприклад: "UA")
   * @returns PhoneCode або null
   */
  const getCountryByIso = (isoCode: string): PhoneCode | null => {
    return PhoneCodes.find((c) => c.isoCode === isoCode) ?? null
  }

  /**
   * Визначає країну по повному номеру
   * @param fullNumber - Повний номер у міжнародному форматі (наприклад: "+380634050445" або "380634050445")
   * @returns PhoneCode або null
   */
  const resolveCountryByFullNumber = (fullNumber: string): PhoneCode | null => {
    if (!fullNumber) {
      return null
    }

    const digitsOnly = fullNumber.replace(/[^\d]/g, '')
    if (digitsOnly.length < 2) {
      return null
    }

    return findCountryByCode(digitsOnly)
  }

  return {
    parseModelValue,
    formatToModelValue,
    normalizeToDisplay,
    formatFromDisplay,
    extractPhoneCode,
    extractNumber,
    getCountryByIso,
    resolveCountryByFullNumber,
  }
}

